import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import { obterElegiveisParaProcurador } from '@/features/proposta/entities/Comprador/opcoes-comprador-menu'
import { BasePessoaFisicaUseCase } from '@/features/proposta/entities/Comprador/use-case/base-pessoa-fisica-use-case'
import type { TClienteRegimeCasamentoEnum } from '@/features/proposta/interfaces/proposta.enums'
import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { TClientePessoaFisicaPropostaSchema, TProcuradorPropostaSchema } from '@/features/proposta/schemas'
import type { CompradorVinculoStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import { formatRadios } from '@/features/proposta/utils/format-radios'
import { origemTelefone, type TEstadoCivilEnum, type TOrigemTelefone } from '@/shared/interfaces/cliente.enums'
import type { TCliente } from '@/shared/interfaces/cliente.types'

export type FetchResult<TPatch> = { type: 'SUCCESS'; patch: TPatch } | { type: 'NOT_FOUND' } | { type: 'ERROR' }

export type TClienteReutilizavelProcurador = {
  tipo: 'Comprador' | 'Mãe/Pai' | 'Cônjuge' | 'Representante Legal' | 'Procurador'
  imagem: string | null
  dados: {
    id: string
    cpf: string
    nome: string
    maiorDe18Anos: 'sim' | 'nao'
    profissaoId: string
    nacionalidadeId: string
    buscouCpf: boolean
    email: string | null
  }
  contato: {
    telefone: {
      numero: string
      origem: TOrigemTelefone
      principal: boolean
    } | null
    email: string | null
  }
  estadoCivil: TEstadoCivilEnum | null
  regimeCasamento: TClienteRegimeCasamentoEnum | null
  possuiUniaoEstavel: boolean | null
}

export class ProcuradorUseCase extends BasePessoaFisicaUseCase<CompradorPessoaJuridicaSchemaInput['responsavel']> {
  constructor(
    private readonly compradores: TCompradoresCadastrados,
    private readonly emailsJaVinculados: Record<string, string>,
    private readonly nacionalidadePrincipalId: string,
    private readonly procuradores?: TProcuradorPropostaSchema[],
  ) {
    super()
  }

  validarSeCpfGeraListaVinculosVazia(id: string) {
    const listaVinculos = obterElegiveisParaProcurador(this.compradores, this.procuradores, id)
    if (listaVinculos.length === 0) {
      return { ok: false, reason: 'NO_ELIGIBLE_LINKS' }
    }
    return { ok: true }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TCliente, AxiosError>,
  ): FetchResult<CompradorVinculoStepSchemaInput> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) {
      return { type: 'NOT_FOUND' }
    }
    if (result.isSuccess && result.data) {
      const cliente = result.data
      return {
        type: 'SUCCESS',
        patch: {
          emailsJaVinculados: this.emailsJaVinculados,
          mode: 'create',
          compradorVinculadoIds: [],
          tipo: 'Procurador',
          tipoDoCliente: 'Contato',
          contato: {
            email: cliente.email ?? '',
            telefone: {
              origem: cliente.telefone?.origem ?? 'Brasil',
              numero: cliente.telefone?.numero ?? '',
              principal: true,
            },
          },
          estadoCivil: {
            status: cliente.estadoCivil ?? null,
            regimeCasamento: cliente.regimeCasamento ?? null,
            uniaoEstavel: formatRadios(cliente.possuiUniaoEstavel),
          },
          dados: {
            id: cliente.id,
            cpf: cliente.cpf ?? '',
            nome: cliente.nome,
            email: null,
            profissaoId: cliente.profissao?.id ?? '',
            nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
            maiorDe18Anos: 'sim',
            buscouCpf: true,
          },
          imagem: cliente.imagem ?? null,
        },
      }
    }

    return { type: 'ERROR' }
  }

  limparProcurador(): CompradorVinculoStepSchemaInput {
    return {
      emailsJaVinculados: this.emailsJaVinculados,
      mode: 'create',
      compradorVinculadoIds: [],
      tipoDoCliente: 'Contato',
      tipo: 'Procurador',
      estadoCivil: {
        status: null,
        regimeCasamento: null,
        uniaoEstavel: null,
        tipoAssinatura: null,
      },
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: '',
        profissaoId: '',
        nacionalidadeId: this.nacionalidadePrincipalId,
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
    }
  }

  static iniciarDefaultValues(
    emailsJaVinculados: Record<string, string>,
    nacionalidadePrincipalId?: string,
  ): CompradorVinculoStepSchemaInput {
    return {
      emailsJaVinculados,
      mode: 'create',
      compradorVinculadoIds: [],
      tipoDoCliente: 'Contato',
      tipo: 'Procurador',
      estadoCivil: {
        status: null,
        regimeCasamento: null,
        uniaoEstavel: null,
        tipoAssinatura: null,
      },
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: null,
        profissaoId: '',
        nacionalidadeId: nacionalidadePrincipalId ?? '',
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
    }
  }

  private static readonly resolveTipoCliente = (
    isProcurador: boolean,
    tipoBase?: 'PessoaFisica' | 'PessoaJuridica' | 'MaePai' | 'Conjuge' | 'Representante',
  ) => {
    if (tipoBase === 'PessoaFisica' || tipoBase === 'PessoaJuridica') return 'Comprador' as const
    if (tipoBase === 'MaePai') return 'Mãe/Pai' as const
    if (tipoBase === 'Conjuge') return 'Cônjuge' as const
    if (tipoBase === 'Representante') return 'Representante Legal' as const
    return isProcurador ? ('Procurador' as const) : ('Comprador' as const)
  }

  private static converterUniaoEstavel(possuiUniaoEstavel?: boolean | null): 'sim' | 'nao' | null {
    if (possuiUniaoEstavel === null || possuiUniaoEstavel === undefined) {
      return null
    }
    return possuiUniaoEstavel ? 'sim' : 'nao'
  }

  static readonly extrairClientesReutilizaveisComoProcurador = (
    compradores: TCompradoresCadastrados,
    procuradores: TProcuradorPropostaSchema[],
    clientes: TClientePessoaFisicaPropostaSchema[],
  ): TClienteReutilizavelProcurador[] => {
    const procuradorIds = new Set(procuradores.map((p) => p.procuradorId))
    const tipoPorId = new Map([
      ...compradores.flatMap((comprador) =>
        comprador.tipo !== 'PessoaJuridica' ? [[comprador.id, comprador.tipo] as const] : [],
      ),
      ...compradores.flatMap((comprador) => comprador.vinculos.map((vinculo) => [vinculo.id, vinculo.tipo] as const)),
    ])

    const resultado = clientes.map((cliente) => {
      const tipoBase = tipoPorId.get(cliente.id)
      const tipo = this.resolveTipoCliente(procuradorIds.has(cliente.id), tipoBase)

      return {
        tipo,
        imagem: cliente.imagem ?? null,
        dados: {
          id: cliente.id,
          cpf: cliente.cpf,
          nome: cliente.nome,
          maiorDe18Anos: cliente.maiorDe18Anos ? ('sim' as const) : ('nao' as const),
          profissaoId: cliente.profissaoId,
          nacionalidadeId: cliente.nacionalidadeId,
          buscouCpf: Boolean(cliente.cpf),
          email: cliente.email,
        },
        contato: {
          telefone: cliente.telefone,
          email: cliente.email,
        },
        estadoCivil: cliente.estadoCivil ?? null,
        regimeCasamento: cliente.regimeCasamento ?? null,
        possuiUniaoEstavel: cliente.possuiUniaoEstavel ?? null,
      }
    })

    return resultado
      .filter((r) => r.dados.maiorDe18Anos === 'sim')
      .sort((a, b) => a.dados.nome.localeCompare(b.dados.nome, 'pt-BR', { sensitivity: 'base' }))
  }

  static inicializarProcuradorComClienteExistente(
    compradoresReutilizaveis: TClienteReutilizavelProcurador[],
    emailsJaVinculados: Record<string, string>,
    reset: (data: CompradorVinculoStepSchemaInput) => void,
  ) {
    return (value: string) => {
      const compradorSelecionado = compradoresReutilizaveis.find((comprador) => comprador.dados.id === value)
      if (!compradorSelecionado) return

      reset({
        emailsJaVinculados,
        mode: 'create',
        compradorVinculadoIds: [],
        contato: {
          telefone: compradorSelecionado.contato?.telefone ?? null,
          email: compradorSelecionado.contato?.email ?? null,
        },
        dados: {
          maiorDe18Anos: 'sim',
          nome: compradorSelecionado.dados.nome,
          buscouCpf: compradorSelecionado.dados.buscouCpf,
          cpf: compradorSelecionado.dados.cpf,
          nacionalidadeId: compradorSelecionado.dados.nacionalidadeId,
          profissaoId: compradorSelecionado.dados.profissaoId,
          email: null,
          id: compradorSelecionado.dados.id,
        },
        estadoCivil: {
          status: compradorSelecionado.estadoCivil,
          tipoAssinatura: null,
          regimeCasamento: compradorSelecionado.regimeCasamento,
          uniaoEstavel: this.converterUniaoEstavel(compradorSelecionado.possuiUniaoEstavel),
        },
        imagem: compradorSelecionado.imagem,
        tipoDoCliente: 'Contato',
        tipo: 'Procurador',
      })
    }
  }
}
